<template>
    <div
        ref="menuRef"
        class="header-menu-mobile"
    >
        <div
            :class="classTopArrived"
            class="header"
        >
            <ButtonRound
                class="button-back"
                icon="angle-left"
                type="link"
                aria-label="Go back one level"
                @click="backProgrammatic"
            />

            <ButtonRound
                class="button-close"
                icon="close"
                type="link"
                aria-label="Close menu"
                @click="close"
            />
        </div>

        <Transition
            :css="false"
            @enter="onEnter"
            @leave="onLeave"
            @before-enter="onBeforeEnter"
            @after-enter="onAfterEnter"
        >
            <SubMenuSection
                v-if="currentPane.__typename === 'SubMenuSection'"
                ref="sectionRef"
                :section="currentPane"
                class="sub-menu-section"
                @close="close"
            />

            <SubMenu
                v-else-if="currentPane.__typename === 'SubMenu'"
                ref="sectionRef"
                :sub-menu="currentPane"
                class="sub-menu"
                @close="close"
                @select="forward"
            />

            <MainMenu
                v-else
                ref="sectionRef"
                :items="mainMenu"
                class="main-menu"
                @close="close"
                @select="forward"
            />
        </Transition>

        <TopBar
            :class="classBottomArrived"
            class="top-bar"
            @toggle-login-form="toggleLoginForm"
        />
    </div>
</template>

<script setup lang="ts">
import { type Ref, computed, ref, watch } from 'vue'
import { usePrevious, useScroll } from '@vueuse/core'
import { last } from 'remeda'

import type {
    HeaderMenuFragment,
    SubMenuFragment,
    SubMenuSectionFragment,
} from '@/graphql/api'
import { useHeaderMenuTransitionHooks } from './mobile/useHeaderMenuTransitionHooks'
import { getTimeout } from '@/helpers/globals'
import { useHideBodyOverflow } from '@/composables/useHideBodyOverflow'
import { assertNever } from '@/helpers'

import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import SubMenuSection from './SubMenuSection.vue'
import MainMenu from './mobile/MainMenu.vue'
import SubMenu from './mobile/SubMenu.vue'
import TopBar from '../TopBar.vue'

type HeaderMenuWrapper = {
    __typename: 'HeaderMenuWrapper'
    items: HeaderMenuFragment[]
}

type Menus =
    | [HeaderMenuWrapper]
    | [HeaderMenuWrapper, SubMenuFragment]
    | [HeaderMenuWrapper, SubMenuFragment, SubMenuSectionFragment]

const props = defineProps<{
    mainMenu: HeaderMenuFragment[]
}>()

const emit = defineEmits<{
    toggleLoginForm: []
    close: []
    areSubMenusClosed: [closed: boolean]
}>()

useHideBodyOverflow()

const toggleLoginForm = () => emit('toggleLoginForm')

const menus = ref<Menus>([
    { __typename: 'HeaderMenuWrapper', items: props.mainMenu },
])
const currentPane = computed(() => last(menus.value))

const close = () => emit('close')

const back = () => {
    const length = menus.value.length

    switch (length) {
        case 1:
            close()

            break
        case 2:
            menus.value = [menus.value[0]]

            break
        case 3:
            menus.value = [menus.value[0], menus.value[1]]

            break
        default:
            assertNever(length, length)
    }
}

const forward = (menu: SubMenuFragment | SubMenuSectionFragment) => {
    const length = menus.value.length

    switch (length) {
        case 1:
            if (menu.__typename === 'SubMenu') {
                menus.value = [menus.value[0], menu]
            }

            break
        case 2:
            if (menu.__typename === 'SubMenuSection') {
                menus.value = [menus.value[0], menus.value[1], menu]
            }

            break
        case 3:
            break
        default:
            assertNever(length, length)
    }
}

// restore previous state in case back swipe is cancelled
const menusPrevious = usePrevious(menus)
const revert = () => (menus.value = menusPrevious.value || menus.value)

const areSubMenusOpen = computed(() => menus.value.length > 1)
const { setTimeout } = getTimeout() ?? { setTimeout: undefined }

// prevents the parent from triggering a transition initially
// after the next tick, the composable will not propagate the event
watch(areSubMenusOpen, (value) =>
    setTimeout?.(() => emit('areSubMenusClosed', !value))
)

const menuRef: Ref<HTMLElement | null> = ref(null)
const { onBeforeEnter, onEnter, onAfterEnter, onLeave, backProgrammatic } =
    useHeaderMenuTransitionHooks(menuRef, back, revert, areSubMenusOpen)

// shadows for menus exceeding viewport height
const sectionRef = ref<{ $el: HTMLElement } | null>(null)
const sectionElement = computed(() => sectionRef.value?.$el)

const { arrivedState } = useScroll(sectionElement)

watch(sectionElement, (value) => {
    if (value) {
        arrivedState.top = true
        arrivedState.bottom = false
    }
})

const classTopArrived = computed(() => ({
    'top-arrived': arrivedState.top,
}))

const classBottomArrived = computed(() => ({
    'bottom-arrived':
        arrivedState.bottom ||
        sectionElement.value?.scrollHeight ===
            sectionElement.value?.clientHeight,
}))
</script>

<style lang="scss" scoped>
.header-menu-mobile {
    position: fixed;
    width: 100%;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 100;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    color: $slate;
    background: $stone-2;
    box-shadow:
        0 3px 6px rgb(0 0 0 / 16%),
        0 3px 6px rgb(0 0 0 / 23%);
    user-select: none;

    @include min-width(1024) {
        display: none;
    }

    .header {
        top: 0;
        position: absolute;
        width: 100%;
        height: 70px;
        padding: 0 10px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background-color: $stone-2;
        z-index: 2;
    }

    .header,
    .top-bar {
        box-shadow:
            0 14px 28px rgb(0 0 0 / 25%),
            0 10px 10px rgb(0 0 0 / 22%);
        transition: box-shadow 175ms var(--timing-function-easing-accelerate);

        &.top-arrived,
        &.bottom-arrived {
            box-shadow: none;
        }
    }

    .top-bar {
        display: block;
        box-shadow:
            0 -14px 28px rgb(0 0 0 / 25%),
            0 -10px 10px rgb(0 0 0 / 22%);
    }

    .sub-menu-section,
    .sub-menu,
    .main-menu {
        position: fixed;
        top: 70px;
        bottom: 70px;
        width: 100%;
        overflow-y: auto;
        background-color: $stone-2;
        box-shadow:
            0 3px 6px rgb(0 0 0 / 16%),
            0 3px 6px rgb(0 0 0 / 23%);
    }

    .main-menu {
        box-shadow: none;
    }
}

.slide-enter-active,
.slide-leave-active,
.slide-reverse-enter-active,
.slide-reverse-leave-active,
.slide-cancelled-leave-active {
    transition: transform 175ms var(--timing-function-easing-accelerate);
}

.slide-enter-from,
.slide-reverse-leave-to {
    transform: translate3d(100%, 0, 0);
    z-index: 1;
}

.slide-reverse-enter-from {
    transform: translate3d(-20%, 0, 0);
}

.slide-leave-to,
.slide-cancelled-leave-to {
    transform: translate3d(-20%, 0, 0);
}
</style>
